<template>
  <tr :class="{ 'blue-grey darken-2': processed }">
    <td colspan="3">
      <v-form ref="form">
        <v-row align="center" class="my-0">
          <v-col cols="4" class="text-truncate">
            <a
              :href="'https://play.google.com/store/apps/details?id=' + model.pattern"
              target="_blank"
              class="text-subtitle-1"
            >
              {{ model.pattern }}
            </a>
          </v-col>
          <v-col cols="2" class="text-truncate">
            <v-menu v-if="item.app_names.length > 1" offset-y max-height="300">
              <template #activator="{ on }">
                <a class="white--text" v-on="on">
                  {{ item.app_names[0] }}
                  <v-icon dense>mdi-chevron-down</v-icon>
                </a>
              </template>
              <v-list dense>
                <v-list-item v-for="(name, index) in item.app_names" :key="index" dense>
                  <v-list-item-title>{{ name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <span v-else>{{ item.app_names[0] }}</span>
          </v-col>
          <v-col cols="3">
            <v-row no-gutters align="center">
              <v-col>
                <app-dict-search
                  v-model="model.app_dictionary_id"
                  :rules="rules.appId"
                  :disabled="processed || loading"
                  inline
                />
              </v-col>
              <v-col v-if="canViewMav()" cols="auto">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="ai-predict-button ml-2"
                      :loading="aiLoading"
                      :disabled="processed || loading"
                      v-bind="attrs"
                      v-on="on"
                      @click="predictWithMav"
                    >
                      <v-icon small color="white">mdi-robot</v-icon>
                    </v-btn>
                  </template>
                  <span>Predict App with MAV</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
          <nv-show :header="model.effective_start_date ? 'Start Date' : ''" cols="1" no-placeholder>
            <owl-date-picker
              v-model="model.effective_start_date"
              :disabled="processed || loading"
              label="Start Date"
              icon
              clearable
            />
            <span v-if="model.effective_start_date" class="mr-1">
              {{ model.effective_start_date }}
            </span>
          </nv-show>
          <v-col
            v-if="item.locked_by === $auth.user.email"
            cols="2"
            class="d-flex justify-end align-center"
          >
            <template v-if="onTime(item.lock_expires_at)">
              <save-btn v-if="!processed" :loading="loading" class="ml-4" @click="save()" />
              <done-chip v-else />
            </template>
            <expire-chip v-else />
          </v-col>
        </v-row>
      </v-form>
    </td>
  </tr>
</template>

<script>
import MAV from '@/api/mav';
import AppDictSearch from '@/components/mv/AppDictSearch';
import ClassifyRowMixin from '@/components/cls/ClassifyRowMixin';
import OwlDatePicker from '@/components/OwlDatePicker';
import Rules from 'bsm-validation-rules';
import SaveBtn from '@/components/cls/SaveBtn.vue';
import DoneChip from '@/components/cls/DoneChip.vue';
import ExpireChip from '@/components/cls/ExpireChip.vue';
import { getConfigMap } from '@/helpers/filters';
import { submitFeedback } from '@/api/feedback';

const rules = {
  appId: [Rules.presence()],
};

export default {
  components: { ExpireChip, DoneChip, SaveBtn, OwlDatePicker, AppDictSearch },
  mixins: [ClassifyRowMixin],
  data() {
    return {
      model: {
        pattern: this.item.package_id,
        app_dictionary_id: null,
        effective_start_date: null,
      },
      formattedPayload: {},
      rules: {
        appId: [],
      },
      aiLoading: false,
      aiSuggestionFull: null,
      feedbackSent: false,
    };
  },
  methods: {
    canViewMav() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return mavConfig?.app_enabled?.v1 || false;
    },

    isShadowMode() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return (
        mavConfig?.enabled?.shadow === true && !mavConfig?.enabled?.v1 && !mavConfig?.enabled?.v2
      );
    },

    isProductionMode() {
      const config = this.getConfigMap();
      const mavConfig = config['mavFeature_owl_ui'];
      return (mavConfig?.enabled?.v1 || mavConfig?.enabled?.v2) && !mavConfig?.enabled?.shadow;
    },

    async predictWithMav() {
      this.aiLoading = true;
      try {
        const response = await this.callMavAPI(this.item.package_id);
        this.aiSuggestionFull = response;
        if (response.application_id) {
          this.model.app_dictionary_id = response.application_id;
        }
      } catch (error) {
        console.error('AI prediction failed:', error);
        this.$flash('error', 'Failed to predict with MAV AI');
      } finally {
        this.aiLoading = false;
      }
    },

    async callMavAPI(packageName) {
      try {
        if (this.isShadowMode()) {
          const response = await MAV.post('/shadow/predict/android', {
            package_name: packageName,
          });
          return response.data.ai_suggestion || response.data;
        }

        const countries = this.item.countries ? this.item.countries.join(',') : undefined;

        const response = await MAV.post('/predict/android', {
          package_name: packageName,
          ...(countries && { countries }),
        });

        const prediction = response.data.prediction || response.data;

        // Build hierarchy rationale similar to domain flow
        let hierarchyLevel = '';
        let hierarchyName = '';
        if (prediction.channel_id && prediction.channel_id !== 0) {
          hierarchyLevel = 'Channel';
          hierarchyName = prediction.channel_name;
        } else if (prediction.brand_id && prediction.brand_id !== 0) {
          hierarchyLevel = 'Brand';
          hierarchyName = prediction.brand_name;
        } else if (prediction.parent_id && prediction.parent_id !== 0) {
          hierarchyLevel = 'Parent';
          hierarchyName = prediction.parent_name;
        }

        return {
          application_id: prediction.application_id,
          application_name: prediction.application_name,
          confidence: prediction.overall_confidence,
          rationale: `${prediction.application_name ||
            'Unknown'} — ${hierarchyLevel}: ${hierarchyName} (Parent: ${prediction.parent_name ||
            'N/A'}, Brand: ${prediction.brand_name || 'N/A'}, Channel: ${prediction.channel_name ||
            'N/A'})`,
          parent_id: prediction.parent_id,
          parent_name: prediction.parent_name,
          brand_id: prediction.brand_id,
          brand_name: prediction.brand_name,
          channel_id: prediction.channel_id,
          channel_name: prediction.channel_name,
          ad_supported: prediction.ad_supported || '',
          search_query: prediction.search_query || packageName,
          entity_type: 'android',
        };
      } catch (error) {
        console.error('MAV API error:', error);
        throw error;
      }
    },

    async sendShadowEvaluation(packageName, expectedAppId) {
      try {
        await MAV.post('/shadow/predict/android', {
          package_name: packageName,
          expected_application_id: expectedAppId,
        });
      } catch (error) {
        console.error('Shadow API error:', error);
      }
    },

    async sendFeedback(action, finalAppId) {
      try {
        await submitFeedback({
          input_string: this.item.package_id,
          ai_suggestion: {
            application_id: this.aiSuggestionFull?.application_id,
            rationale: this.aiSuggestionFull?.rationale || '',
            confidence: this.aiSuggestionFull?.confidence || 0,
            entity_type: 'android',
          },
          user_final_decision: {
            application_id: finalAppId,
            entity_type: 'android',
          },
          action,
        });
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },

    save() {
      this.rules = rules;
      this.formattedPayload = this.model;

      // Send feedback if AI was used
      if (this.aiSuggestionFull && this.model.app_dictionary_id) {
        if (this.isProductionMode() && !this.feedbackSent) {
          const action =
            this.aiSuggestionFull.application_id === this.model.app_dictionary_id
              ? 'ACCEPTED'
              : 'CORRECTED';
          this.sendFeedback(action, this.model.app_dictionary_id);
          this.feedbackSent = true;
        }
      }

      // Shadow mode evaluation
      if (this.isShadowMode() && this.model.app_dictionary_id) {
        this.sendShadowEvaluation(this.item.package_id, this.model.app_dictionary_id);
      }

      this.submitClassify(`/android/discoveries/${this.item.id}/rules`);
    },

    getConfigMap,
  },
};
</script>

<style scoped>
.ai-predict-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;
}

.ai-predict-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
  box-shadow: 0 6px 12px rgba(102, 126, 234, 0.5) !important;
  transform: translateY(-1px);
}

.ai-predict-button:disabled {
  opacity: 0.5;
}
</style>
